import React, { useState } from 'react';
import {
  TrendingUp, Building2, DollarSign, AlertTriangle,
  ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import { Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
import { clsx } from 'clsx';
import type {
  BillingHistoryResponse,
  CompanyBillingData,
  DasCalculation,
} from '../../types/billingHistory';

// ─── Formatting helpers ─────────────────────────────────────────

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

export const formatPercent = (value: number): string =>
  `${(value * 100).toFixed(2)}%`;

export const formatCNPJ = (doc: string): string => {
  const d = doc.replace(/\D/g, '').padStart(14, '0');
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12, 14)}`;
};

export const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// ─── Chart tooltip ──────────────────────────────────────────────

export const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((entry: any, idx: number) => (
        <p key={idx} style={{ color: entry.color }} className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
          {entry.name}: {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

// ─── Billing bar+line chart (shared) ────────────────────────────

const BillingChart: React.FC<{ data: { name: string; 'Faturamento Líquido': number; 'Vendas': number; 'Devoluções': number }[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart data={data}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
      <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
      <Tooltip content={<CustomTooltip />} />
      <Legend />
      <Bar dataKey="Vendas" fill="#10b981" radius={[4, 4, 0, 0]} />
      <Bar dataKey="Devoluções" fill="#ef4444" radius={[4, 4, 0, 0]} />
      <Line type="monotone" dataKey="Faturamento Líquido" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
    </ComposedChart>
  </ResponsiveContainer>
);

// ─── Consolidated summary card (shared) ─────────────────────────

export const ConsolidatedBillingCard: React.FC<{
  data: BillingHistoryResponse;
  totalLabel: string;
}> = ({ data, totalLabel }) => {
  const chartData = data.consolidated.map(m => ({
    name: m.label,
    'Faturamento Líquido': m.netBilling,
    'Vendas': m.totalSales,
    'Devoluções': m.totalReturns,
  }));

  return (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={20} className="text-emerald-600" />
        <h2 className="text-lg font-semibold text-emerald-900">Consolidado — Todas as Empresas</h2>
      </div>

      {/* Total card */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="bg-white border border-emerald-200 rounded-lg p-4 min-w-[200px]">
          <p className="text-sm text-slate-500">{totalLabel}</p>
          <p className="text-2xl font-bold text-emerald-700">{formatCurrency(data.consolidatedTotal)}</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-lg p-4 min-w-[200px]">
          <p className="text-sm text-slate-500">Faturamento Mensal Médio</p>
          <p className="text-2xl font-bold text-emerald-700">
            {formatCurrency(data.consolidatedTotal / (data.consolidated.length || 1))}
          </p>
        </div>
      </div>

      {/* Consolidated Chart */}
      <div className="h-72 bg-white rounded-lg border border-emerald-100 p-2">
        <BillingChart data={chartData} />
      </div>

      {/* Consolidated Table */}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-emerald-200">
              <th className="text-left py-2 px-3 font-semibold text-slate-600">Mês</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Vendas (Saída)</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Devoluções (Entrada)</th>
              <th className="text-right py-2 px-3 font-semibold text-slate-600">Faturamento Líquido</th>
            </tr>
          </thead>
          <tbody>
            {data.consolidated.map((m, idx) => (
              <tr key={idx} className="border-b border-slate-100 hover:bg-white/70">
                <td className="py-2 px-3 text-slate-700">{m.label}</td>
                <td className="py-2 px-3 text-right text-emerald-600">{formatCurrency(m.totalSales)}</td>
                <td className="py-2 px-3 text-right text-red-500">{formatCurrency(m.totalReturns)}</td>
                <td className="py-2 px-3 text-right font-medium text-slate-900">{formatCurrency(m.netBilling)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-emerald-50 font-semibold">
              <td className="py-2 px-3 text-slate-700">Total</td>
              <td className="py-2 px-3 text-right text-emerald-600">
                {formatCurrency(data.consolidated.reduce((s, m) => s + m.totalSales, 0))}
              </td>
              <td className="py-2 px-3 text-right text-red-500">
                {formatCurrency(data.consolidated.reduce((s, m) => s + m.totalReturns, 0))}
              </td>
              <td className="py-2 px-3 text-right text-slate-900">{formatCurrency(data.consolidatedTotal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Company billing section (shared) ───────────────────────────

export const CompanyBillingSection: React.FC<{ company: CompanyBillingData; dasCalc?: DasCalculation }> = ({ company, dasCalc }) => {
  const [expanded, setExpanded] = useState(true);

  const chartData = company.monthlyData.map(m => ({
    name: m.label,
    'Faturamento Líquido': m.netBilling,
    'Vendas': m.totalSales,
    'Devoluções': m.totalReturns,
  }));

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Building2 size={20} className="text-emerald-600" />
          <div className="text-left">
            <h3 className="text-lg font-semibold text-slate-900">{company.companyName}</h3>
            <p className="text-sm text-slate-500">CNPJ: {formatCNPJ(company.document)}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-slate-500">Total do Período</p>
            <p className="text-lg font-bold text-emerald-600">{formatCurrency(company.totalNetBilling)}</p>
          </div>
          {expanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-6 pb-6 space-y-6">
          {/* Chart */}
          <div className="h-72">
            <BillingChart data={chartData} />
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 px-3 font-semibold text-slate-600">Mês</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Vendas (Saída)</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Devoluções (Entrada)</th>
                  <th className="text-right py-2 px-3 font-semibold text-slate-600">Faturamento Líquido</th>
                </tr>
              </thead>
              <tbody>
                {company.monthlyData.map((m, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-slate-700">{m.label}</td>
                    <td className="py-2 px-3 text-right text-emerald-600">{formatCurrency(m.totalSales)}</td>
                    <td className="py-2 px-3 text-right text-red-500">{formatCurrency(m.totalReturns)}</td>
                    <td className="py-2 px-3 text-right font-medium text-slate-900">{formatCurrency(m.netBilling)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td className="py-2 px-3 text-slate-700">Total</td>
                  <td className="py-2 px-3 text-right text-emerald-600">
                    {formatCurrency(company.monthlyData.reduce((s, m) => s + m.totalSales, 0))}
                  </td>
                  <td className="py-2 px-3 text-right text-red-500">
                    {formatCurrency(company.monthlyData.reduce((s, m) => s + m.totalReturns, 0))}
                  </td>
                  <td className="py-2 px-3 text-right text-slate-900">
                    {formatCurrency(company.totalNetBilling)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* DAS Calculation Section */}
          {dasCalc && <DasSection calc={dasCalc} />}
        </div>
      )}
    </div>
  );
};

// ─── DAS Section ────────────────────────────────────────────────

const DasSection: React.FC<{ calc: DasCalculation }> = ({ calc }) => {
  const tierChanged = calc.currentTier !== calc.projectedTier;

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <DollarSign size={20} className="text-blue-600" />
        <h4 className="text-base font-semibold text-blue-900">Cálculo do DAS — Simples Nacional (Anexo I)</h4>
      </div>

      {/* Mês Atual */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="RBT12 (últimos 12 meses)" value={formatCurrency(calc.rbt12)} />
        <StatCard label="Faturamento do Mês" value={formatCurrency(calc.currentMonthBilling)} />
        <StatCard label={`Alíquota Efetiva (${calc.currentTier}ª faixa)`} value={formatPercent(calc.effectiveRate)} />
        <StatCard label="Valor do DAS" value={formatCurrency(calc.dasAmount)} highlight />
      </div>

      {/* Indicador de próxima faixa */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white/70 rounded-lg p-4 border border-blue-100">
        <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          {calc.nextTierRate !== null ? (
            <>
              Faltam <span className="font-bold text-blue-700">{formatCurrency(calc.revenueToNextTier)}</span> de faturamento acumulado
              para mudar da <span className="font-semibold">{calc.currentTier}ª faixa</span> para a{' '}
              <span className="font-semibold">{calc.currentTier + 1}ª faixa</span> (alíquota nominal de{' '}
              <span className="font-semibold">{formatPercent(calc.nextTierRate)}</span>).
            </>
          ) : (
            <span className="font-medium text-slate-600">Empresa já se encontra na última faixa do Anexo I.</span>
          )}
        </div>
      </div>

      {/* Projeção mês seguinte */}
      <div className="bg-white/70 rounded-lg p-4 border border-blue-100">
        <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1">
          {tierChanged ? <ArrowUpRight size={16} className="text-red-500" /> : <ArrowDownRight size={16} className="text-emerald-500" />}
          Projeção para o Mês Seguinte
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MiniStat label="RBT12 Projetado" value={formatCurrency(calc.rbt12Projected)} />
          <MiniStat label={`Faixa Projetada`} value={`${calc.projectedTier}ª faixa`} />
          <MiniStat label="Alíquota Efetiva Projetada" value={formatPercent(calc.projectedEffectiveRate)} />
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={clsx('rounded-lg p-3 border', highlight ? 'bg-blue-600 border-blue-700' : 'bg-white border-slate-200')}>
    <p className={clsx('text-xs', highlight ? 'text-blue-100' : 'text-slate-500')}>{label}</p>
    <p className={clsx('text-lg font-bold mt-1', highlight ? 'text-white' : 'text-slate-900')}>{value}</p>
  </div>
);

const MiniStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-sm font-semibold text-slate-800">{value}</p>
  </div>
);
